import {
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    View
} from "react-native";
import type RequestTabType from "./type";
import stylesheet from "./stylesheet";
import AccountDeletionRequests from "./views/accountDeletionRequests";
import CommercialOpportunityRequests from "./views/campaignRequests";
import CompanyRequests from "./views/companyRequests";
import ContactRequests from "./views/contactRequests";
import ProfileRequests from "./views/profileRequests";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../navigation/type";
import type {
    RouteProp
} from "@react-navigation/native";
import {
    MessageSquareIcon,
    Building2Icon,
    Trash2Icon,
    UserIcon,
    TagIcon
} from "lucide-react-native";

const RequestsPage = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const route = useRoute<RouteProp<RootStackParamList, "Requests">>();

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        activeTab,
        setActiveTab
    ] = useState<RequestTabType>(route.params?.initialTab || "profile");

    const [
        refreshKey
    ] = useState<number>(0);

    useEffect(() => {
        if(route.params?.initialTab) {
            setActiveTab(route.params.initialTab);
        }
    }, [route.params?.initialTab]);

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingXl,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.titleContainer,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleLargeSize"
                >
                    {localize("requests-page-title")}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("requests-page-description")}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.tabsContainer,
                    {
                        backgroundColor: colors.content.container.subtle,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXs,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    variant={activeTab === "profile" ? "filled" : "ghost"}
                    type={activeTab === "profile" ? "primary" : "neutral"}
                    title={localize("requests-tab-profile")}
                    onPress={() => setActiveTab("profile")}
                    icon={({
                        color,
                        size
                    }) => <UserIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                />
                <Button
                    variant={activeTab === "company" ? "filled" : "ghost"}
                    type={activeTab === "company" ? "primary" : "neutral"}
                    title={localize("requests-tab-company")}
                    onPress={() => setActiveTab("company")}
                    icon={({
                        color,
                        size
                    }) => <Building2Icon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                />
                <Button
                    variant={activeTab === "campaign" ? "filled" : "ghost"}
                    type={activeTab === "campaign" ? "primary" : "neutral"}
                    title={localize("requests-tab-campaign")}
                    onPress={() => setActiveTab("campaign")}
                    icon={({
                        color,
                        size
                    }) => <TagIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                />
                <Button
                    variant={activeTab === "deletion" ? "filled" : "ghost"}
                    type={activeTab === "deletion" ? "primary" : "neutral"}
                    title={localize("requests-tab-deletion")}
                    onPress={() => setActiveTab("deletion")}
                    icon={({
                        color,
                        size
                    }) => <Trash2Icon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                />
                <Button
                    variant={activeTab === "contact" ? "filled" : "ghost"}
                    type={activeTab === "contact" ? "primary" : "neutral"}
                    title={localize("requests-tab-contact")}
                    onPress={() => setActiveTab("contact")}
                    icon={({
                        color,
                        size
                    }) => <MessageSquareIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    spreadBehaviour="free"
                />
            </View>
        </View>;
    };

    const renderContent = () => {
        if(activeTab === "company") {
            return <CompanyRequests
                refreshKey={refreshKey}
            />;
        }

        if(activeTab === "campaign") {
            return <CommercialOpportunityRequests
                refreshKey={refreshKey}
            />;
        }

        if(activeTab === "deletion") {
            return <AccountDeletionRequests
                refreshKey={refreshKey}
            />;
        }

        if(activeTab === "contact") {
            return <ContactRequests
                refreshKey={refreshKey}
            />;
        }

        return <ProfileRequests
            refreshKey={refreshKey}
        />;
    };

    return <PageContainer
        isCustomPadding={true}
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: isMobile ? spaces.spacingMd : spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            <View
                style={stylesheet.contentContainer}
            >
                {renderContent()}
            </View>
        </View>
    </PageContainer>;
};
export default RequestsPage;
